import { useCallback, useEffect, useState } from 'react';
import { Gavel, RefreshCw } from 'lucide-react';
import PageShell from '../components/PageShell';
import StatCard from '../components/StatCard';
import DataTable, { type Column } from '../components/DataTable';
import StatusPill from '../components/StatusPill';
import { secondaryButtonClass } from '../components/FilterBar';
import { formatCurrency, formatDate, titleCase } from '../lib/format';
import { fetchAssetRegister } from '../lib/api';

type Row = Record<string, unknown>;

function str(row: Row, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && value !== '' && typeof value !== 'object') {
      return String(value);
    }
  }
  return null;
}

const PRIORITY = ['asset_name', 'name', 'asset_category', 'item_name', 'gross_purchase_amount', 'purchase_date', 'status'];

function buildColumns(rows: Row[]): Column<Row>[] {
  const known: Column<Row>[] = [
    { key: 'asset_name', header: 'Asset', render: (r) => str(r, 'asset_name', 'name', 'item_name') ?? '—' },
    { key: 'asset_category', header: 'Category', render: (r) => str(r, 'asset_category', 'category') ?? '—' },
    {
      key: 'gross_purchase_amount',
      header: 'Purchase value',
      align: 'right',
      render: (r) => formatCurrency(str(r, 'gross_purchase_amount', 'purchase_amount', 'value')),
    },
    { key: 'purchase_date', header: 'Purchased', render: (r) => formatDate(str(r, 'purchase_date', 'available_for_use_date')) },
    { key: 'status', header: 'Status', render: (r) => <StatusPill status={str(r, 'status') ?? 'active'} /> },
  ];

  const hasKnown = rows.some((r) =>
    PRIORITY.some((k) => r[k] !== undefined && r[k] !== null)
  );
  if (hasKnown || rows.length === 0) return known;

  const keys = new Set<string>();
  for (const row of rows.slice(0, 10)) {
    for (const key of Object.keys(row)) {
      if (str(row, key) !== null && key !== 'id') keys.add(key);
    }
  }
  return [...keys].slice(0, 6).map((key) => ({
    key,
    header: titleCase(key),
    render: (r: Row) => str(r, key) ?? '—',
  }));
}

function Assets() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const result = await fetchAssetRegister();
    if (result.ok && result.data) setRows(result.data.data ?? []);
    else setRows([]);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const totalValue = rows.reduce((sum, r) => {
    const value = Number(str(r, 'gross_purchase_amount', 'purchase_amount', 'value') ?? 0);
    return sum + (Number.isNaN(value) ? 0 : value);
  }, 0);

  return (
    <PageShell
      title="Assets"
      subtitle="Fixed asset register synced from ERPNext."
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Assets" value={loading ? '…' : String(rows.length)} tone="info" icon={<Gavel size={18} />} />
        <StatCard label="Total value" value={loading ? '…' : formatCurrency(totalValue)} tone="neutral" />
        <StatCard label="Source" value="ERPNext" tone="neutral" />
        <StatCard label="Register" value={loading ? '…' : 'Loaded'} tone="success" />
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-zt-muted">Fixed asset register</div>
        <button type="button" onClick={load} className={secondaryButtonClass}>
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      <DataTable
        columns={buildColumns(rows)}
        rows={rows}
        rowKey={(r, i) => str(r, 'name', 'asset_name', 'id') ?? String(i)}
        loading={loading}
        emptyTitle="No assets"
        emptyMessage="Connect ERPNext to populate the asset register."
        emptyIcon={<Gavel size={32} />}
      />
    </PageShell>
  );
}

export default Assets;
